import { useState, useCallback, useRef } from 'react'
import { artifactService } from '@/services'
import { useLegacyUpdatesStream } from '@/hooks/useLegacyUpdatesStream'
import type { LegacyUpdateEvent } from '@/hooks/useLegacyUpdatesStream'
import type { Artifact, ArtifactType } from '@/types/api'

export type GenerationStatus = 'idle' | 'requesting' | 'generating' | 'completed' | 'failed'

export interface ArtifactGeneration {
  artifactId: string
  type: ArtifactType
  status: GenerationStatus
  artifact: Artifact | null
  error: string | null
}

/**
 * Hook for generating artifacts and tracking their status via SSE.
 *
 * Returns a list of in-flight / completed generations and a `generate()` function.
 * Subscribes to `useLegacyUpdatesStream` filtered to the active artifact IDs.
 */
export function useArtifactGeneration() {
  const [generations, setGenerations] = useState<ArtifactGeneration[]>([])
  const generationsRef = useRef(generations)
  generationsRef.current = generations

  /** IDs currently in-flight for SSE subscription */
  const activeIds = generations
    .filter((g) => g.status === 'generating' || g.status === 'requesting')
    .map((g) => g.artifactId)

  // ── SSE: track artifact status updates
  useLegacyUpdatesStream({
    entityTypes: ['artifact'],
    entityIds: activeIds.length > 0 ? activeIds : undefined,
    enabled: activeIds.length > 0,
    onUpdate: useCallback(async (event: LegacyUpdateEvent) => {
      const id = event.entityId
      const current = generationsRef.current.find((g) => g.artifactId === id)
      if (!current) return

      if (event.status === 'completed') {
        // Fetch the actual artifact content
        try {
          const artifact = await artifactService.getArtifact(id)
          setGenerations((prev) =>
            prev.map((g) =>
              g.artifactId === id ? { ...g, status: 'completed', artifact } : g
            )
          )
        } catch {
          setGenerations((prev) =>
            prev.map((g) =>
              g.artifactId === id ? { ...g, status: 'completed' } : g
            )
          )
        }
      } else if (event.status === 'failed') {
        setGenerations((prev) =>
          prev.map((g) =>
            g.artifactId === id
              ? { ...g, status: 'failed', error: String(event.payload?.error || 'Generation failed') }
              : g
          )
        )
      } else if (event.status === 'processing') {
        setGenerations((prev) =>
          prev.map((g) =>
            g.artifactId === id ? { ...g, status: 'generating' } : g
          )
        )
      }
    }, []),
  })

  /** Kick off artifact generation. Returns the artifactId. */
  const generate = useCallback(async (type: ArtifactType): Promise<string | null> => {
    const placeholder: ArtifactGeneration = {
      artifactId: `pending-${Date.now()}`,
      type,
      status: 'requesting',
      artifact: null,
      error: null,
    }

    setGenerations((prev) => [...prev, placeholder])

    try {
      const res = await artifactService.generateArtifact(type)
      const artifactId = res.artifactId || res.artifact?.id

      if (!artifactId) {
        setGenerations((prev) =>
          prev.map((g) =>
            g.artifactId === placeholder.artifactId
              ? { ...g, status: 'failed', error: 'No artifact ID returned' }
              : g
          )
        )
        return null
      }

      // Replace placeholder with real ID, move to 'generating'
      setGenerations((prev) =>
        prev.map((g) =>
          g.artifactId === placeholder.artifactId
            ? { ...g, artifactId, status: 'generating' }
            : g
        )
      )

      return artifactId
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to generate artifact'
      setGenerations((prev) =>
        prev.map((g) =>
          g.artifactId === placeholder.artifactId
            ? { ...g, status: 'failed', error: errorMsg }
            : g
        )
      )
      return null
    }
  }, [])

  /** Remove a generation from the list (dismiss). */
  const dismiss = useCallback((artifactId: string) => {
    setGenerations((prev) => prev.filter((g) => g.artifactId !== artifactId))
  }, [])

  return { generations, generate, dismiss }
}
